/**
 * @license
 * Copyright 2025 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

// Code generated by the Google Gen AI SDK generator DO NOT EDIT.

import * as common from '../_common.js';
import type * as types from '../types.js';

export function createFileSearchStoreConfigToMldev(
  fromObject: types.CreateFileSearchStoreConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromDisplayName = common.getValueByPath(fromObject, ['displayName']);
  if (parentObject !== undefined && fromDisplayName != null) {
    common.setValueByPath(parentObject, ['displayName'], fromDisplayName);
  }

  return toObject;
}

export function createFileSearchStoreParametersToMldev(
  fromObject: types.CreateFileSearchStoreParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    createFileSearchStoreConfigToMldev(fromConfig, toObject);
  }

  return toObject;
}

export function deleteFileSearchStoreConfigToMldev(
  fromObject: types.DeleteFileSearchStoreConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromForce = common.getValueByPath(fromObject, ['force']);
  if (parentObject !== undefined && fromForce != null) {
    common.setValueByPath(parentObject, ['_query', 'force'], fromForce);
  }

  return toObject;
}

export function deleteFileSearchStoreParametersToMldev(
  fromObject: types.DeleteFileSearchStoreParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['_url', 'name'], fromName);
  }

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    deleteFileSearchStoreConfigToMldev(fromConfig, toObject);
  }

  return toObject;
}

export function getFileSearchStoreParametersToMldev(
  fromObject: types.GetFileSearchStoreParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['_url', 'name'], fromName);
  }

  return toObject;
}

export function importFileConfigToMldev(
  fromObject: types.ImportFileConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromCustomMetadata = common.getValueByPath(fromObject, [
    'customMetadata',
  ]);
  if (parentObject !== undefined && fromCustomMetadata != null) {
    let transformedList = fromCustomMetadata;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(parentObject, ['customMetadata'], transformedList);
  }

  const fromChunkingConfig = common.getValueByPath(fromObject, [
    'chunkingConfig',
  ]);
  if (parentObject !== undefined && fromChunkingConfig != null) {
    common.setValueByPath(parentObject, ['chunkingConfig'], fromChunkingConfig);
  }

  return toObject;
}

export function importFileOperationFromMldev(
  fromObject: types.ImportFileOperation,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromName = common.getValueByPath(fromObject, ['name']);
  if (fromName != null) {
    common.setValueByPath(toObject, ['name'], fromName);
  }

  const fromMetadata = common.getValueByPath(fromObject, ['metadata']);
  if (fromMetadata != null) {
    common.setValueByPath(toObject, ['metadata'], fromMetadata);
  }

  const fromDone = common.getValueByPath(fromObject, ['done']);
  if (fromDone != null) {
    common.setValueByPath(toObject, ['done'], fromDone);
  }

  const fromError = common.getValueByPath(fromObject, ['error']);
  if (fromError != null) {
    common.setValueByPath(toObject, ['error'], fromError);
  }

  const fromResponse = common.getValueByPath(fromObject, ['response']);
  if (fromResponse != null) {
    common.setValueByPath(
      toObject,
      ['response'],
      importFileResponseFromMldev(fromResponse),
    );
  }

  return toObject;
}

export function importFileParametersToMldev(
  fromObject: types.ImportFileParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromFileSearchStoreName = common.getValueByPath(fromObject, [
    'fileSearchStoreName',
  ]);
  if (fromFileSearchStoreName != null) {
    common.setValueByPath(
      toObject,
      ['_url', 'file_search_store_name'],
      fromFileSearchStoreName,
    );
  }

  const fromFileName = common.getValueByPath(fromObject, ['fileName']);
  if (fromFileName != null) {
    common.setValueByPath(toObject, ['fileName'], fromFileName);
  }

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    importFileConfigToMldev(fromConfig, toObject);
  }

  return toObject;
}

export function importFileResponseFromMldev(
  fromObject: types.ImportFileResponse,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromParent = common.getValueByPath(fromObject, ['parent']);
  if (fromParent != null) {
    common.setValueByPath(toObject, ['parent'], fromParent);
  }

  const fromDocumentName = common.getValueByPath(fromObject, ['documentName']);
  if (fromDocumentName != null) {
    common.setValueByPath(toObject, ['documentName'], fromDocumentName);
  }

  return toObject;
}

export function listFileSearchStoresConfigToMldev(
  fromObject: types.ListFileSearchStoresConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromPageSize = common.getValueByPath(fromObject, ['pageSize']);
  if (parentObject !== undefined && fromPageSize != null) {
    common.setValueByPath(parentObject, ['_query', 'pageSize'], fromPageSize);
  }

  const fromPageToken = common.getValueByPath(fromObject, ['pageToken']);
  if (parentObject !== undefined && fromPageToken != null) {
    common.setValueByPath(parentObject, ['_query', 'pageToken'], fromPageToken);
  }

  return toObject;
}

export function listFileSearchStoresParametersToMldev(
  fromObject: types.ListFileSearchStoresParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    listFileSearchStoresConfigToMldev(fromConfig, toObject);
  }

  return toObject;
}

export function listFileSearchStoresResponseFromMldev(
  fromObject: types.ListFileSearchStoresResponse,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  const fromNextPageToken = common.getValueByPath(fromObject, [
    'nextPageToken',
  ]);
  if (fromNextPageToken != null) {
    common.setValueByPath(toObject, ['nextPageToken'], fromNextPageToken);
  }

  const fromFileSearchStores = common.getValueByPath(fromObject, [
    'fileSearchStores',
  ]);
  if (fromFileSearchStores != null) {
    let transformedList = fromFileSearchStores;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(toObject, ['fileSearchStores'], transformedList);
  }

  return toObject;
}

export function uploadToFileSearchStoreConfigToMldev(
  fromObject: types.UploadToFileSearchStoreConfig,
  parentObject: Record<string, unknown>,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromMimeType = common.getValueByPath(fromObject, ['mimeType']);
  if (parentObject !== undefined && fromMimeType != null) {
    common.setValueByPath(parentObject, ['mimeType'], fromMimeType);
  }

  const fromDisplayName = common.getValueByPath(fromObject, ['displayName']);
  if (parentObject !== undefined && fromDisplayName != null) {
    common.setValueByPath(parentObject, ['displayName'], fromDisplayName);
  }

  const fromCustomMetadata = common.getValueByPath(fromObject, [
    'customMetadata',
  ]);
  if (parentObject !== undefined && fromCustomMetadata != null) {
    let transformedList = fromCustomMetadata;
    if (Array.isArray(transformedList)) {
      transformedList = transformedList.map((item) => {
        return item;
      });
    }
    common.setValueByPath(parentObject, ['customMetadata'], transformedList);
  }

  const fromChunkingConfig = common.getValueByPath(fromObject, [
    'chunkingConfig',
  ]);
  if (parentObject !== undefined && fromChunkingConfig != null) {
    common.setValueByPath(parentObject, ['chunkingConfig'], fromChunkingConfig);
  }

  return toObject;
}

export function uploadToFileSearchStoreParametersToMldev(
  fromObject: types.UploadToFileSearchStoreParameters,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromFileSearchStoreName = common.getValueByPath(fromObject, [
    'fileSearchStoreName',
  ]);
  if (fromFileSearchStoreName != null) {
    common.setValueByPath(
      toObject,
      ['_url', 'file_search_store_name'],
      fromFileSearchStoreName,
    );
  }

  const fromConfig = common.getValueByPath(fromObject, ['config']);
  if (fromConfig != null) {
    uploadToFileSearchStoreConfigToMldev(fromConfig, toObject);
  }

  return toObject;
}

export function uploadToFileSearchStoreResumableResponseFromMldev(
  fromObject: types.UploadToFileSearchStoreResumableResponse,
): Record<string, unknown> {
  const toObject: Record<string, unknown> = {};

  const fromSdkHttpResponse = common.getValueByPath(fromObject, [
    'sdkHttpResponse',
  ]);
  if (fromSdkHttpResponse != null) {
    common.setValueByPath(toObject, ['sdkHttpResponse'], fromSdkHttpResponse);
  }

  return toObject;
}
